import { useCallback, useState } from "react";
import { useI18n } from "@expertloop/i18n/react";
import type { AnalyticsPeriodKey, AnalyticsTransactionBasis } from "@expertloop/shared";
import { financeApi } from "../features/finance/api";
import { CurrencyTotals } from "../features/finance/CurrencyTotals";
import { TransactionsTable } from "../features/finance/TransactionsTable";
import { ContributionSection } from "../features/finance/ContributionSection";
import { useAdminSection } from "../features/admin-sections/useAdminSection";
import { SectionShell } from "../features/admin-sections/SectionShell";
import { PeriodPicker } from "../features/admin-sections/PeriodPicker";
import { ActivitySection } from "../features/overview/ActivitySection";
import "./OverviewPage.css";
import "./FinancePage.css";

/**
 * Finance de la place de marché.
 *
 * La page ne montre que ce qui a réellement été encaissé, devise par devise, et
 * relie chaque total aux commandes qui le composent. Elle dit explicitement ce
 * qu'elle ne sait pas encore mesurer, parce qu'une page financière silencieuse
 * sur ses angles morts se lit comme une page complète.
 */
export function FinancePage() {
	const { t } = useI18n();
	const [period, setPeriod] = useState<AnalyticsPeriodKey>("30d");
	const [currency, setCurrency] = useState<string | null>(null);
	// Ventes encaissées ou remboursements aboutis : deux lectures de la même
	// période, bornées par deux dates différentes.
	const [basis, setBasis] = useState<AnalyticsTransactionBasis>("capture");
	const [page, setPage] = useState(1);

	const marketplace = useAdminSection(
		useCallback(() => financeApi.marketplace(period), [period]),
		[period],
	);
	const series = useAdminSection(useCallback(() => financeApi.series(period), [period]), [period]);
	// La consolidation se charge pour son propre compte : la finance de la place
	// de marché reste lisible même si elle est indisponible.
	const consolidation = useAdminSection(
		useCallback(() => financeApi.consolidation(period), [period]),
		[period],
	);
	const transactions = useAdminSection(
		useCallback(
			() => financeApi.transactions(period, currency, basis, page),
			[period, currency, basis, page],
		),
		[period, currency, basis, page],
	);

	const currencies =
		marketplace.state.status === "ready"
			? marketplace.state.data.byCurrency.map((row) => row.currency)
			: [];

	/** Changer de période ou de devise ramène au début de la liste. */
	const changePeriod = (next: AnalyticsPeriodKey) => {
		setPeriod(next);
		setPage(1);
	};

	const changeCurrency = (next: string | null) => {
		setCurrency(next);
		setPage(1);
	};

	const changeBasis = (next: AnalyticsTransactionBasis) => {
		setBasis(next);
		setPage(1);
	};

	return (
		<div className="admin-overview admin-finance">
			<div className="page-header">
				<h1>{t("adminFinance.title")}</h1>
				<p>{t("adminFinance.subtitle")}</p>
			</div>

			<PeriodPicker value={period} onChange={changePeriod} />

			<p className="admin-finance__scope">{t("adminFinance.scopeNotice")}</p>

			<section className="admin-overview__section">
				<h2>{t("adminFinance.gmv")}</h2>
				<SectionShell section={marketplace}>
					{(data) => <CurrencyTotals marketplace={data} />}
				</SectionShell>
			</section>

			<section className="admin-overview__section">
				<h2>{t("adminOverview.activityTitle")}</h2>
				<SectionShell section={series}>{(data) => <ActivitySection series={data} />}</SectionShell>
			</section>

			<section className="admin-overview__section">
				<h2>{t("adminFinance.contributionTitle")}</h2>
				<SectionShell section={consolidation}>
					{(data) => <ContributionSection consolidation={data} />}
				</SectionShell>
			</section>

			<section className="admin-overview__section">
				<h2>{t("adminFinance.transactionsTitle")}</h2>
				<p className="admin-overview__state">
					{t(basis === "refund" ? "adminFinance.refundsHint" : "adminFinance.transactionsHint")}
				</p>

				<div className="admin-finance__filter">
					<label htmlFor="finance-basis">{t("adminFinance.basisLabel")}</label>
					<select
						id="finance-basis"
						value={basis}
						onChange={(event) => changeBasis(event.target.value as AnalyticsTransactionBasis)}
					>
						<option value="capture">{t("adminFinance.basisCapture")}</option>
						<option value="refund">{t("adminFinance.basisRefund")}</option>
					</select>
				</div>

				<div className="admin-finance__filter">
					<label htmlFor="finance-currency">{t("adminFinance.currencyFilter")}</label>
					<select
						id="finance-currency"
						value={currency ?? ""}
						onChange={(event) => changeCurrency(event.target.value || null)}
					>
						<option value="">{t("adminFinance.allCurrencies")}</option>
						{currencies.map((code) => (
							<option key={code} value={code}>
								{code.toUpperCase()}
							</option>
						))}
					</select>
				</div>

				<SectionShell section={transactions}>
					{(data) => <TransactionsTable page={data} onPageChange={setPage} />}
				</SectionShell>
			</section>
		</div>
	);
}
